import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardBody, Alert, Spinner, Button, Input, Select } from '@kollektive/design-system';
import { Check, X, Building2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useRegistration, type CheckSlugResponse } from '@/hooks/useRegistration';
import { ApiClientError } from '@/api';
import styles from './RegisterSetupPage.module.css';

// Tax ID configuration by country
interface TaxIdConfig {
  label: string;
  placeholder: string;
  pattern: RegExp;
  example: string;
}

const TAX_ID_CONFIG: Record<string, TaxIdConfig> = {
  // South Africa
  ZA: { label: 'VAT number', placeholder: '4123456789', pattern: /^4\d{9}$/, example: '4123456789' },
  // USA
  US: { label: 'EIN', placeholder: '12-3456789', pattern: /^\d{2}-?\d{7}$/, example: '12-3456789' },
  // UK
  GB: { label: 'VAT number', placeholder: 'GB123456789', pattern: /^GB(\d{9}|\d{12}|(GD|HA)\d{3})$/, example: 'GB123456789' },
  // EU countries
  AT: { label: 'VAT number', placeholder: 'ATU12345678', pattern: /^ATU\d{8}$/, example: 'ATU12345678' },
  BE: { label: 'VAT number', placeholder: 'BE0123456789', pattern: /^BE0\d{9}$/, example: 'BE0123456789' },
  DK: { label: 'VAT number', placeholder: 'DK12345678', pattern: /^DK\d{8}$/, example: 'DK12345678' },
  FI: { label: 'VAT number', placeholder: 'FI12345678', pattern: /^FI\d{8}$/, example: 'FI12345678' },
  FR: { label: 'VAT number', placeholder: 'FRXX123456789', pattern: /^FR[A-Z0-9]{2}\d{9}$/, example: 'FRXX123456789' },
  DE: { label: 'VAT number', placeholder: 'DE123456789', pattern: /^DE\d{9}$/, example: 'DE123456789' },
  IE: { label: 'VAT number', placeholder: 'IE1234567X', pattern: /^IE\d{7}[A-Z]{1,2}$/, example: 'IE1234567X' },
  IT: { label: 'VAT number', placeholder: 'IT12345678901', pattern: /^IT\d{11}$/, example: 'IT12345678901' },
  NL: { label: 'VAT number', placeholder: 'NL123456789B01', pattern: /^NL\d{9}B\d{2}$/, example: 'NL123456789B01' },
  PT: { label: 'VAT number', placeholder: 'PT123456789', pattern: /^PT\d{9}$/, example: 'PT123456789' },
  ES: { label: 'VAT number', placeholder: 'ESX12345678', pattern: /^ES[A-Z0-9]\d{7}[A-Z0-9]$/, example: 'ESX12345678' },
  SE: { label: 'VAT number', placeholder: 'SE123456789001', pattern: /^SE\d{12}$/, example: 'SE123456789001' },
  // Australasia
  AU: { label: 'ABN', placeholder: '12 345 678 901', pattern: /^\d{2}\s?\d{3}\s?\d{3}\s?\d{3}$/, example: '12 345 678 901' },
  NZ: { label: 'GST number', placeholder: '123-456-789', pattern: /^\d{3}-?\d{3}-?\d{3}$/, example: '123-456-789' },
};

// Default tax ID config for countries not in the list
const DEFAULT_TAX_ID_CONFIG: TaxIdConfig = {
  label: 'Tax ID',
  placeholder: 'Enter your tax ID',
  pattern: /.+/,
  example: '',
};

// Target countries grouped by region
const COUNTRY_OPTIONS = [
  // Australasia
  { value: 'AU', label: 'Australia', group: 'Australasia' },
  { value: 'NZ', label: 'New Zealand', group: 'Australasia' },
  // Africa
  { value: 'ZA', label: 'South Africa', group: 'Africa' },
  // Europe - UK
  { value: 'GB', label: 'United Kingdom', group: 'Europe' },
  // Europe - EU
  { value: 'AT', label: 'Austria', group: 'European Union' },
  { value: 'BE', label: 'Belgium', group: 'European Union' },
  { value: 'DK', label: 'Denmark', group: 'European Union' },
  { value: 'FI', label: 'Finland', group: 'European Union' },
  { value: 'FR', label: 'France', group: 'European Union' },
  { value: 'DE', label: 'Germany', group: 'European Union' },
  { value: 'IE', label: 'Ireland', group: 'European Union' },
  { value: 'IT', label: 'Italy', group: 'European Union' },
  { value: 'NL', label: 'Netherlands', group: 'European Union' },
  { value: 'PT', label: 'Portugal', group: 'European Union' },
  { value: 'ES', label: 'Spain', group: 'European Union' },
  { value: 'SE', label: 'Sweden', group: 'European Union' },
  // North America
  { value: 'US', label: 'United States', group: 'North America' },
];

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Generate slug from tenant name
function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 50);
}

export function RegisterSetupPage() {
  const navigate = useNavigate();
  const { checkSlug, register, isCheckingSlug, isRegistering, error, clearError } = useRegistration();

  const [tenantName, setTenantName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugStatus, setSlugStatus] = useState<CheckSlugResponse | null>(null);
  const [country, setCountry] = useState('');
  const [registeredBusinessName, setRegisteredBusinessName] = useState('');
  const [businessAddress, setBusinessAddress] = useState('');
  const [taxId, setTaxId] = useState('');
  const [taxIdError, setTaxIdError] = useState<string | null>(null);
  const [session, setSession] = useState<{ access_token: string } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // Get tax ID configuration based on selected country
  const taxIdConfig = country ? (TAX_ID_CONFIG[country] || DEFAULT_TAX_ID_CONFIG) : DEFAULT_TAX_ID_CONFIG;

  // Debounce slug for availability check
  const debouncedSlug = useDebounce(slug, 500);

  // Get session on mount
  useEffect(() => {
    const getSession = async () => {
      const { data } = await supabase.auth.getSession();
      if (!data.session) {
        // Not authenticated, redirect to register
        navigate('/register');
        return;
      }
      setSession(data.session);
    };
    getSession();
  }, [navigate]);

  // Auto-generate slug from tenant name if slug hasn't been manually edited
  useEffect(() => {
    if (!slugTouched && tenantName) {
      setSlug(generateSlug(tenantName));
    }
  }, [tenantName, slugTouched]);

  // Check slug availability when debounced slug changes
  useEffect(() => {
    if (debouncedSlug.length >= 3) {
      checkSlugAvailability(debouncedSlug);
    } else {
      setSlugStatus(null);
    }
  }, [debouncedSlug]);

  // Clear tax ID error when country changes and validate if tax ID has value
  useEffect(() => {
    if (taxId && country) {
      validateTaxId(taxId);
    } else {
      setTaxIdError(null);
    }
  }, [country]);

  // Validate tax ID format
  const validateTaxId = (value: string): boolean => {
    if (!value.trim()) {
      setTaxIdError('Tax ID is required');
      return false;
    }
    if (!country) {
      setTaxIdError(null);
      return true;
    }
    const config = TAX_ID_CONFIG[country] || DEFAULT_TAX_ID_CONFIG;
    const normalizedValue = value.toUpperCase().trim();
    if (!config.pattern.test(normalizedValue)) {
      setTaxIdError(`Invalid format. Example: ${config.example}`);
      return false;
    }
    setTaxIdError(null);
    return true;
  };

  const handleTaxIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTaxId(value);
    if (value) {
      validateTaxId(value);
    } else {
      setTaxIdError(null);
    }
  };

  const checkSlugAvailability = async (slugToCheck: string) => {
    try {
      const result = await checkSlug(slugToCheck);
      setSlugStatus(result);
    } catch {
      // Error handled by hook
    }
  };

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSlug = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setSlug(newSlug);
    setSlugTouched(true);
    setSlugStatus(null);
  };

  const handleSuggestionClick = useCallback(() => {
    if (slugStatus?.suggestion) {
      setSlug(slugStatus.suggestion);
      setSlugTouched(true);
      // Trigger availability check for suggestion
      checkSlugAvailability(slugStatus.suggestion);
    }
  }, [slugStatus?.suggestion]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    clearError();

    if (!session?.access_token) {
      setFormError('Not authenticated. Please sign in again.');
      return;
    }

    if (!tenantName.trim()) {
      setFormError('Workspace name is required');
      return;
    }

    if (slug.length < 3) {
      setFormError('Slug must be at least 3 characters');
      return;
    }

    if (!slugStatus?.available) {
      setFormError('Please choose an available URL');
      return;
    }

    if (!country) {
      setFormError('Country is required');
      return;
    }

    if (!registeredBusinessName.trim()) {
      setFormError('Registered business name is required');
      return;
    }

    if (!businessAddress.trim()) {
      setFormError('Business address is required');
      return;
    }

    if (!taxId.trim()) {
      setFormError(`${taxIdConfig.label} is required`);
      return;
    }

    if (!validateTaxId(taxId)) {
      setFormError(`Invalid ${taxIdConfig.label} format`);
      return;
    }

    try {
      const result = await register(session.access_token, {
        tenant_name: tenantName.trim(),
        slug: slug,
        country: country,
        registered_business_name: registeredBusinessName.trim(),
        business_address: businessAddress.trim(),
        tax_id: taxId.trim().toUpperCase(),
      });

      // Redirect to the tenant's dashboard
      // In production, this would redirect to the subdomain
      // For development, we'll redirect with the tenant param
      const host = window.location.hostname;
      if (host === 'localhost' || host === '127.0.0.1') {
        navigate(`/dashboard?tenant=${result.tenant.slug}`);
      } else {
        // Redirect to subdomain in production
        window.location.href = result.tenant.subdomain_url;
      }
    } catch (err) {
      if (err instanceof ApiClientError) {
        setFormError(err.message);
      } else {
        setFormError('Failed to create workspace. Please try again.');
      }
    }
  };

  const isFormValid =
    tenantName.trim().length >= 2 &&
    slug.length >= 3 &&
    slugStatus?.available &&
    country !== '' &&
    registeredBusinessName.trim().length >= 2 &&
    businessAddress.trim().length >= 5 &&
    taxId.trim().length >= 3 &&
    !taxIdError;

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {/* Logo and branding */}
        <div className={styles.branding}>
          <div className={styles.logoPlaceholder}>
            <Building2 size={32} />
          </div>
          <h1 className={styles.title}>Set up your workspace</h1>
          <p className={styles.subtitle}>
            Choose a name and URL for your workspace
          </p>
        </div>

        {/* Setup card */}
        <Card>
          <CardBody className={styles.cardBody}>
            <form onSubmit={handleSubmit} className={styles.form}>
              {/* Error message */}
              {(formError || error) && (
                <Alert variant="error" styleVariant="glass" className={styles.error}>
                  {formError || error?.message}
                </Alert>
              )}

              {/* Workspace name input */}
              <div className={styles.inputGroup}>
                <label htmlFor="tenantName" className={styles.label}>
                  Workspace name
                </label>
                <Input
                  id="tenantName"
                  type="text"
                  placeholder="Acme Corporation"
                  value={tenantName}
                  onChange={(e) => setTenantName(e.target.value)}
                  disabled={isRegistering}
                  autoFocus
                />
              </div>

              {/* Slug input with availability indicator */}
              <div className={styles.inputGroup}>
                <label htmlFor="slug" className={styles.label}>
                  Workspace URL
                </label>
                <div className={styles.slugInputWrapper}>
                  <Input
                    id="slug"
                    type="text"
                    placeholder="acme"
                    value={slug}
                    onChange={handleSlugChange}
                    disabled={isRegistering}
                    className={styles.slugInput}
                  />
                  <span className={styles.slugSuffix}>.kollektive.ai</span>
                  <div className={styles.slugStatus}>
                    {isCheckingSlug && <Spinner size="sm" />}
                    {!isCheckingSlug && slugStatus?.available && (
                      <Check size={18} className={styles.slugAvailable} />
                    )}
                    {!isCheckingSlug && slugStatus && !slugStatus.available && (
                      <X size={18} className={styles.slugTaken} />
                    )}
                  </div>
                </div>
                {slugStatus && !slugStatus.available && slugStatus.suggestion && (
                  <p className={styles.slugSuggestion}>
                    Try{' '}
                    <button
                      type="button"
                      onClick={handleSuggestionClick}
                      className={styles.suggestionLink}
                    >
                      {slugStatus.suggestion}
                    </button>{' '}
                    instead
                  </p>
                )}
                {slug.length > 0 && slug.length < 3 && (
                  <p className={styles.slugHint}>Slug must be at least 3 characters</p>
                )}
              </div>

              {/* Country select */}
              <div className={styles.inputGroup}>
                <label htmlFor="country" className={styles.label}>
                  Country
                </label>
                <Select
                  value={country}
                  onChange={(value) => setCountry(value)}
                  disabled={isRegistering}
                  options={COUNTRY_OPTIONS}
                  placeholder="Select a country"
                  searchable
                />
              </div>

              {/* Registered business name input */}
              <div className={styles.inputGroup}>
                <label htmlFor="registeredBusinessName" className={styles.label}>
                  Registered business name
                </label>
                <Input
                  id="registeredBusinessName"
                  type="text"
                  placeholder="Acme Corporation Pty Ltd"
                  value={registeredBusinessName}
                  onChange={(e) => setRegisteredBusinessName(e.target.value)}
                  disabled={isRegistering}
                />
              </div>

              {/* Business address input */}
              <div className={styles.inputGroup}>
                <label htmlFor="businessAddress" className={styles.label}>
                  Registered business address
                </label>
                <Input
                  id="businessAddress"
                  type="text"
                  placeholder="123 Business St, Suite 100, City, State, ZIP"
                  value={businessAddress}
                  onChange={(e) => setBusinessAddress(e.target.value)}
                  disabled={isRegistering}
                />
              </div>

              {/* Tax ID input - label changes based on country */}
              <div className={styles.inputGroup}>
                <label htmlFor="taxId" className={styles.label}>
                  {taxIdConfig.label}
                </label>
                <Input
                  id="taxId"
                  type="text"
                  placeholder={taxIdConfig.placeholder}
                  value={taxId}
                  onChange={handleTaxIdChange}
                  disabled={isRegistering}
                />
                {taxIdError && (
                  <p className={styles.fieldError}>{taxIdError}</p>
                )}
              </div>

              {/* Submit button */}
              <Button
                type="submit"
                variant="primary"
                className={styles.submitButton}
                disabled={!isFormValid || isRegistering}
              >
                {isRegistering ? (
                  <>
                    <Spinner size="sm" />
                    <span>Creating workspace...</span>
                  </>
                ) : (
                  'Create workspace'
                )}
              </Button>
            </form>
          </CardBody>
        </Card>

        {/* Powered by */}
        <p className={styles.poweredBy}>
          Powered by <span className={styles.poweredByBrand}>Kollektive</span>
        </p>
      </div>
    </div>
  );
}
